import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';
import { getYearLabel as getYearLabelUtil } from '../utils/yearUtils';

const ParentConsentForms = ({ parentEmail, parentName, students, onBack }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [myResponses, setMyResponses] = useState([]);
  const [viewingHistory, setViewingHistory] = useState(false);
  const [selectedHistoryResponse, setSelectedHistoryResponse] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'completed'

  useEffect(() => {
    loadForms();
    loadMyResponses();
  }, []);

  const loadForms = async () => {
    try {
      setLoading(true);
      // Get student IDs for filtering
      const studentIds = students.map(s => s.school_id).join(',');
      console.log('[ParentConsentForms] Loading forms for student IDs:', studentIds);
      console.log('[ParentConsentForms] Students data:', students);
      const response = await axios.get(buildApiUrl(`/api/consent-forms/for-students?student_ids=${studentIds}`));
      console.log('[ParentConsentForms] API Response:', response.data);
      if (response.data.success) {
        console.log('[ParentConsentForms] Forms received:', response.data.forms);
        response.data.forms.forEach(form => {
          console.log(`[ParentConsentForms] Form "${form.title}":`, {
            target_years: form.target_years,
            target_students: form.target_students,
            hasTargeting: (form.target_years && form.target_years.length > 0) || (form.target_students && form.target_students.length > 0)
          });
        });
        setForms(response.data.forms || []);
      }
    } catch (error) {
      console.error('Failed to load consent forms:', error);
      setMessage({ type: 'error', text: 'Failed to load consent forms' });
    } finally {
      setLoading(false);
    }
  };

  const loadMyResponses = async () => {
    try {
      const response = await axios.get(buildApiUrl(`/api/consent-forms/responses?parent_email=${parentEmail}`));
      if (response.data.success) {
        setMyResponses(response.data.responses || []);
      }
    } catch (error) {
      console.error('Failed to load responses:', error);
    }
  };

  const hasSubmittedForm = (formId, studentId) => {
    return myResponses.some(r => r.form_id === formId && r.student_school_id === studentId);
  };

  // Check if a specific student is targeted by a form
  const isStudentTargeted = (form, student) => {
    const hasYearTargeting = form.target_years && form.target_years.length > 0;
    const hasStudentTargeting = form.target_students && form.target_students.length > 0;

    // If no targeting at all, form doesn't apply to anyone
    if (!hasYearTargeting && !hasStudentTargeting) {
      return false;
    }

    // Check if student ID is directly in target_students
    if (hasStudentTargeting && form.target_students.includes(student.school_id)) {
      return true;
    }

    // Check if student's year group is in target_years
    if (hasYearTargeting) {
      // Extract year group from student data
      let studentYear = null;
      
      // Try to get from nc_year field
      if (student.nc_year !== undefined && student.nc_year !== null) {
        studentYear = getYearLabelUtil(student.nc_year);
      }
      
      // Fallback: extract from form field (e.g., "3S" -> "Year 3", "R" -> "Reception")
      if (!studentYear && student.form) {
        const formStr = student.form.toString().trim();
        if (/^PS/i.test(formStr)) studentYear = 'Pre-School';
        else if (/^R$/i.test(formStr)) studentYear = 'Reception';
        else {
          const yearMatch = formStr.match(/^(\d+)/);
          if (yearMatch) studentYear = `Year ${yearMatch[1]}`;
        }
      }

      if (studentYear && form.target_years.includes(studentYear)) {
        return true;
      }
    }

    return false;
  };

  const handleStartForm = (form, student) => {
    setSelectedForm(form);
    setSelectedStudent(student);
    // Initialize responses object
    const initialResponses = {};
    form.questions.forEach(q => {
      initialResponses[q.id] = q.type === 'multiple_choice' ? [] : '';
    });
    setResponses(initialResponses);
    setMessage({ type: '', text: '' });
  };

  const handleResponseChange = (questionId, value, questionType) => {
    if (questionType === 'multiple_choice') {
      setResponses(prev => {
        const current = prev[questionId] || [];
        const updated = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        return { ...prev, [questionId]: updated };
      });
    } else {
      setResponses(prev => ({ ...prev, [questionId]: value }));
    }
  };

  const validateForm = () => {
    const requiredQuestions = selectedForm.questions.filter(q => q.required);
    for (let question of requiredQuestions) {
      const response = responses[question.id];
      if (!response || (Array.isArray(response) && response.length === 0)) {
        return { valid: false, message: `Please answer: ${question.question}` };
      }
    }
    return { valid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      setMessage({ type: 'error', text: validation.message });
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        form_id: selectedForm.id,
        student_school_id: selectedStudent.school_id,
        student_name: selectedStudent.full_name,
        parent_email: parentEmail,
        parent_name: parentName,
        responses: responses
      };

      const response = await axios.post(buildApiUrl('/api/consent-forms/responses'), payload);
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Form submitted successfully!' });
        setTimeout(() => {
          setSelectedForm(null);
          setSelectedStudent(null);
          setResponses({});
          loadMyResponses(); // Reload to update submitted forms
        }, 2000);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to submit form' });
      }
    } catch (error) {
      console.error('Failed to submit form:', error);
      setMessage({ type: 'error', text: 'Failed to submit form. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedForm(null);
    setSelectedStudent(null);
    setResponses({});
    setMessage({ type: '', text: '' });
    setViewingHistory(false);
    setSelectedHistoryResponse(null);
  };

  const viewResponseDetails = (response) => {
    setSelectedHistoryResponse(response);
    setViewingHistory(true);
  };

  const getFormById = (formId) => {
    return forms.find(f => f.id === formId);
  };

  const formatResponseValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (value === 'yes') return 'Yes';
    if (value === 'no') return 'No';
    return value || 'No answer';
  };

  // Render question input based on type
  const renderQuestionInput = (question) => {
    const value = responses[question.id] || (question.type === 'multiple_choice' ? [] : '');

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value, 'text')}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Your answer"
          />
        );

      case 'paragraph':
        return (
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value, 'paragraph')}
            rows="4"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Your answer"
          />
        );

      case 'boolean':
        return (
          <div className="flex gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name={`question-${question.id}`}
                value="yes"
                checked={value === 'yes'}
                onChange={(e) => handleResponseChange(question.id, 'yes', 'boolean')}
                className="mr-2"
              />
              <span>Yes</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name={`question-${question.id}`}
                value="no"
                checked={value === 'no'}
                onChange={(e) => handleResponseChange(question.id, 'no', 'boolean')}
                className="mr-2"
              />
              <span>No</span>
            </label>
          </div>
        );

      case 'single_choice':
        return (
          <div className="space-y-2">
            {question.options.map((option, idx) => (
              <label key={idx} className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleResponseChange(question.id, option, 'single_choice')}
                  className="mr-2"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {question.options.map((option, idx) => (
              <label key={idx} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  value={option}
                  checked={value.includes(option)}
                  onChange={(e) => handleResponseChange(question.id, option, 'multiple_choice')}
                  className="mr-2"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );

      case 'upload':
        return (
          <div>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Convert file to base64
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    // Save the base64 data URL (includes mime type and data)
                    handleResponseChange(question.id, reader.result, 'upload');
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {value && value.startsWith('data:') && (
              <p className="text-sm text-green-600 mt-1">✓ File uploaded</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Viewing response details
  if (viewingHistory && selectedHistoryResponse) {
    const form = getFormById(selectedHistoryResponse.form_id);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-0 sm:p-6">
        <div className="w-full sm:max-w-3xl mx-auto">
          <div className="bg-white rounded-none sm:rounded-2xl shadow-xl p-4 sm:p-8">
            {/* Header */}
            <div className="mb-6">
              <button
                onClick={handleCancel}
                className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
              >
                ← Back to Forms
              </button>
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-3xl font-bold text-gray-800">{form?.title || 'Consent Form'}</h1>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                  ✓ Submitted
                </span>
              </div>
              {form?.description && (
                <p className="text-gray-600 mb-4">{form.description}</p>
              )}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-sm text-blue-800">
                  <strong>Student:</strong> {selectedHistoryResponse.student_name} ({selectedHistoryResponse.student_school_id})
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  <strong>Submitted:</strong> {new Date(selectedHistoryResponse.submitted_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Responses - Read Only */}
            <div className="space-y-6">
              {form?.questions.map((question, idx) => {
                const answer = selectedHistoryResponse.responses[question.id];
                return (
                  <div key={question.id} className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-start">
                      <span className="bg-gray-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">
                          {question.question}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">
                          {question.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                          <p className="text-gray-800 font-medium">{formatResponseValue(answer)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form filling view
  if (selectedForm && selectedStudent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-0 sm:p-6">
        <div className="w-full sm:max-w-3xl mx-auto">
          <div className="bg-white rounded-none sm:rounded-2xl shadow-xl p-4 sm:p-8">
            {/* Header */}
            <div className="mb-8">
              <button
                onClick={handleCancel}
                className="text-blue-600 hover:text-blue-700 mb-6 flex items-center"
              >
                ← Back to Forms
              </button>
              {/* Logo */}
              {selectedForm.logo_url && (
                <div className="flex justify-center mb-6">
                  <img 
                    src={selectedForm.logo_url} 
                    alt="Form Logo" 
                    className="max-h-24 object-contain"
                  />
                </div>
              )}
              {/* Title as H2 heading */}
              <h2 className="text-4xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-4">{selectedForm.title}</h2>
              {selectedForm.description && (
                <p className="text-gray-600 text-lg mb-4">{selectedForm.description}</p>
              )}
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-3 sm:p-4">
                <p className="text-sm text-blue-800">
                  <strong>Filling for:</strong> {selectedStudent.full_name.split(' ')[0]} ({selectedStudent.school_id})
                </p>
              </div>
            </div>

            {/* Message */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-lg ${
                message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {message.text}
              </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
              {selectedForm.questions.map((question, idx) => (
                <div key={question.id} className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-start mb-4">
                    <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-3 flex-shrink-0">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">
                        {question.question}
                        {question.required && <span className="text-red-500 ml-1">*</span>}
                      </h3>
                      <p className="text-sm text-gray-500 mb-3">
                        {question.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      {renderQuestionInput(question)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Submit Button */}
            <div className="mt-8 flex gap-4">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Form'}
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Forms list view
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-0 sm:p-6">
      <div className="w-full sm:max-w-6xl mx-auto">
        <div className="bg-white rounded-none sm:rounded-2xl shadow-xl p-4 sm:p-8">
          {onBack && (
            <button
              onClick={onBack}
              className="text-blue-600 hover:text-blue-700 mb-4 flex items-center font-semibold"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          )}
          <h1 className="text-3xl font-bold text-gray-800 mb-2">📋 Consent Forms</h1>
          <p className="text-gray-600 mb-8">Complete required consent forms for your children</p>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading forms...</p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="mb-6 border-b border-gray-200">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('pending')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'pending'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    📋 Pending Forms
                    {forms.filter(form => students.some(s => isStudentTargeted(form, s) && !hasSubmittedForm(form.id, s.school_id))).length > 0 && (
                      <span className="ml-2 bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-bold">
                        {forms.filter(form => students.some(s => isStudentTargeted(form, s) && !hasSubmittedForm(form.id, s.school_id))).length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('completed')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'completed'
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    ✅ Completed Forms
                    {myResponses.length > 0 && (
                      <span className="ml-2 bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs font-bold">
                        {myResponses.length}
                      </span>
                    )}
                  </button>
                </nav>
              </div>

              {/* Pending Forms Tab */}
              {activeTab === 'pending' && (
                <>
                  {forms.filter(form => students.some(s => isStudentTargeted(form, s) && !hasSubmittedForm(form.id, s.school_id))).length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🎉</div>
                      <p className="text-gray-600 text-lg font-medium">All forms completed!</p>
                      <p className="text-gray-500 text-sm mt-2">You have no pending consent forms</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {forms.map(form => {
                        // Find students who are targeted AND haven't submitted this form
                        const pendingStudents = students.filter(s => 
                          isStudentTargeted(form, s) && !hasSubmittedForm(form.id, s.school_id)
                        );
                        
                        if (pendingStudents.length === 0) return null;
                        
                        return (
                          <div key={form.id} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                            {form.logo_url && (
                              <div className="bg-gray-50 p-4 flex justify-center">
                                <img src={form.logo_url} alt="Form Logo" className="h-20 object-contain" />
                              </div>
                            )}
                            <div className="p-6">
                              <h3 className="text-xl font-bold text-gray-800 mb-2">{form.title}</h3>
                              {form.description && (
                                <p className="text-gray-600 mb-4">{form.description}</p>
                              )}
                              <div className="space-y-2">
                                {pendingStudents.map(student => (
                                  <div key={student.school_id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg p-3 gap-3 sm:gap-0">
                                    <div className="w-full sm:w-auto">
                                      <p className="font-semibold text-gray-800">{student.full_name.split(' ')[0]}</p>
                                      <p className="text-sm text-gray-600">Form: {student.form}</p>
                                    </div>
                                    <button
                                      onClick={() => handleStartForm(form, student)}
                                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      Fill Form
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Completed Forms Tab */}
              {activeTab === 'completed' && (
                <>
                  {myResponses.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📝</div>
                      <p className="text-gray-600 text-lg font-medium">No completed forms yet</p>
                      <p className="text-gray-500 text-sm mt-2">Forms you submit will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myResponses.map(response => {
                        const form = getFormById(response.form_id);
                        return (
                          <div key={response.id} className="border border-green-200 bg-green-50 rounded-xl p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h3 className="font-bold text-gray-800">{form?.title || 'Consent Form'}</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  Student: {response.student_name} ({response.student_school_id})
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Submitted: {new Date(response.submitted_at).toLocaleDateString()} at {new Date(response.submitted_at).toLocaleTimeString()}
                                </p>
                              </div>
                              <button
                                onClick={() => viewResponseDetails(response)}
                                className="ml-4 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentConsentForms;
